import React, { useState, useEffect, useRef } from 'react'
import api from '../api/api'
import { useNavigate,useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import AnalysisCompanies from './AnalysisCompanies';
import { convertCurrency, exchangeToCurrency } from '../utils/commonUtils';
import ChatArea from './ChatArea';
import NotebookList from '../NotebookList';
import SelectColumnContainer from '../dashboard/components/SelectColumnContainer';
import { currencyToSymbol } from '../utils/commonUtils';
import AnalysisGraphArea from './AnalysisGraphArea';
import { marked } from 'marked';
import { ScatterChart } from 'lucide-react';
const marketAccounts = ['stock_price', 'volume', 'market_value', 'per', 'pbr', 'psr', 'dividend_yield'];
const dividendAccounts = ['dividend', 'payout_ratio'];


const AnalysisArea = ({ selectedCompanies, width='100%', resized, onDeleteClick, onDeleteAllClick, showAnalysisAreaSmall, handleCloseAnalylsis }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [financialData, setFinancialData] = useState([]);
    const [scatterFinancialData, setScatterFinancialData] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [selectedScatterAccounts, setSelectedScatterAccounts] = useState([]);
    const [selectedItem, setSelectedItem] = useState('bar');
    const [showNotebookList, setShowNotebookList] = useState(false);
    const [latestChatHistoryId, setLatestChatHistoryId] = useState(0);
    const [selectedChatHistoryId, setSelectedChatHistoryId] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [scatterGraphData, setScatterGraphData] = useState(null);
    const [chatId, setChatId] = useState(null);
    const notebookListRef = useRef(null); 
    const widthRef = useRef(null); 
    const [divWidth, setDivWidth] = useState(0);
    const [marketData, setMarketData] = useState([]);
    const [latestMarketData, setLatestMarketData] = useState(null);
    const [dividendData, setDividendData] = useState(null);
    
    useEffect(() => {
        if (!widthRef.current) return;
        // ResizeObserverを定義
        const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
            if (entry.contentRect) {
                setDivWidth(entry.contentRect.width); // ref要素の幅を設定
            }
        }
        });

        // ref要素を監視
        observer.observe(widthRef.current);

        // クリーンアップ処理
        return () => {
        if (widthRef.current) {
            observer.unobserve(widthRef.current);
        }
        };
    }, [selectedCompanies]);

    useEffect(() => {
        setChatId(uuidv4());
    }, []);
    
    const itemClick = (item) => {
        setSelectedItem(item);
    }

    const createMarketGraphData = (selectedCompanies, marketDataFetched, account, unit, graphType) => {
        const selectedSecCodes = selectedCompanies.map((company) => company.secCode);
        const divUnits = {};
        const dataMap = {};
        const limitedCompanies = [];
        const currencies = [];
        let processedCount = 0; // 処理した企業数をカウント
        let modifiedAccountCol = account.col === 'stock_price' ? 'close' : account.col;
    
        // **1. 全ての日付を事前に収集**
        const allDates = new Set();
        marketDataFetched.forEach((marketData) => {
            if (selectedSecCodes.includes(marketData.secCode)) {
                marketData.data.forEach((entry) => {
                    allDates.add(entry.date);
                });
            }
        });
    
        // dataMap を全日付で初期化
        const sortedDates = Array.from(allDates).sort(); // 日付をソートしておく
        sortedDates.forEach((date) => {
            dataMap[date] = { date }; // 各日付を初期化
        });
    
        // **2. 企業ごとのデータを格納**
        for (let i = 0; i < marketDataFetched.length; i++) {
            const marketData = marketDataFetched[i];
            if (processedCount >= 5) break; // 5件に達したら中断
    
            if (selectedSecCodes.includes(marketData.secCode)) {
                limitedCompanies.push(selectedCompanies.find(c => c.secCode === marketData.secCode));
                
                // その企業のデータを格納
                marketData.data.forEach((entry) => {
                    const date = entry.date;
                    dataMap[date][marketData.secCode] = entry[modifiedAccountCol];
                });
    
                divUnits[marketData.secCode] = 1;
                const currency = '円';
                currencies.push(currency);
                processedCount++;
            }
        }
    
        // **3. 欠けているデータをnullで埋める**
        Object.values(dataMap).forEach((entry) => {
            selectedSecCodes.forEach((secCode) => {
                if (entry[secCode] === undefined) {
                    entry[secCode] = null; // データがない場合はnullを設定
                }
            });
        });
    
        // **4. チャート用のデータを作成**
        const chartData = {
            data: Object.values(dataMap),
            unit: unit,
            unitX: '',
            divUnit: divUnits,
            currency: currencies,
            col: account.col,
            name: account.col_name,
            graphType: graphType,
            dataCategory: 'market',
            companies: limitedCompanies,
        };
        return chartData;
    };
    
    const createDividendGraphData = (selectedCompanies, dividendDataFetched, account, unit, graphType) => {
        const selectedSecCodes = selectedCompanies.map((company) => company.secCode);
        const yearMap = {};
        const divUnits = {};
        const convertedUnits = {};
        const limitedCompanies = [];
        const currencies = [];
        const accountCol = account.col === 'dividend' ? 'adjdividend' : account.col;
        let processedCount = 0; // 処理した企業数をカウント
    
        // **1. 全年度を事前に収集**
        const allYears = new Set();
        dividendDataFetched.forEach((company) => {
            if (selectedSecCodes.includes(company.secCode)) {
                company.data.forEach((entry) => {
                    allYears.add(entry.fiscal_year);
                });
            }
        });
    
        // `yearMap` を全年度で初期化
        const sortedYears = Array.from(allYears).sort(); // 年度をソート
        sortedYears.forEach((year) => {
            yearMap[year] = { x: year }; // 年度ごとに初期化
        });
    
        // **2. 企業ごとのデータを格納**
        for (let i = 0; i < dividendDataFetched.length; i++) {
            const company = dividendDataFetched[i];
            if (processedCount >= 5) break; // 5件に達したら中断
    
            if (selectedSecCodes.includes(company.secCode)) {
                limitedCompanies.push(selectedCompanies.find(c => c.secCode === company.secCode));
                
                // 各企業のデータを年度ごとに格納
                company.data.forEach((entry) => {
                    const year = entry.fiscal_year;
                    yearMap[year][company.secCode] = entry[accountCol];
                });
    
                // 値を変換して、単位を計算
                const values = company.data.map((entry) => entry[accountCol]);
                const converted = convertCurrency(values);
                const divUnit = converted.divValue;
                const convertedUnit = converted.unit;
    
                divUnits[company.secCode] = divUnit;
                const currency = exchangeToCurrency(company.data[company.data.length - 1].exchangeshortname);
                currencies.push(currency);
    
                const displayUnit = unit === 'value' ? currencyToSymbol(currency) : unit;
                convertedUnits[company.secCode] = convertedUnit + displayUnit;
    
                processedCount++;
            }
        }
    
        // **3. 欠けているデータをnullで埋める**
        Object.values(yearMap).forEach((entry) => {
            selectedSecCodes.forEach((secCode) => {
                if (entry[secCode] === undefined) {
                    entry[secCode] = null; // データがない場合はnullを設定
                }
            });
        });
    
        // **4. チャート用のデータを作成**
        const chartData = {
            data: Object.values(yearMap),
            unit: convertedUnits,
            unitX: '年',
            divUnit: divUnits,
            currency: currencies,
            col: account.col,
            name: account.col_name,
            graphType: graphType,
            dataCategory: 'financial',
            companies: limitedCompanies,
        };
        return chartData;
    };
    
    useEffect(() => {
        const fetchAndProcessData = async () => {
            if ((selectedItem === 'bar' || selectedItem === 'line') & selectedAccounts.length > 0 && selectedCompanies.length > 0) {
                // グラフデータのリセット
                setGraphData([]);
    
                try {
                    // すべての選択された企業の financialData を並行して取得
                    const financialDataFetched = await Promise.all(
                        selectedCompanies.slice(0, 5).map(async (company) => {
                            if (financialData.some((data) => data.secCode === company.secCode)) {
                                // 既に存在するデータを使用
                                return financialData.find(data => data.secCode === company.secCode);
                            } else {
                                // APIリクエストで新しいデータを取得]
                                try {
                                    const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/financial-results/${company.secCode}/`);
                                    const sortedData = response.data.slice().reverse();
                                    return { secCode: company.secCode, data: sortedData };
                                } catch (error) {
                                    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                                        navigate('/login', { state: { from: location.pathname } });
                                    } else {
                                        console.error('Error fetching financial results:', error);
                                    }
                                }
                            }
                        })
                    );
                    // 一度に financialData をセット
                    setFinancialData((prevFinancialData) => [
                        ...prevFinancialData,
                        ...financialDataFetched.filter(
                            (newData) => !prevFinancialData.some((data) => data.secCode === newData.secCode)
                        ),
                    ]);
    
                    let marketDataFetched;
                    if (selectedAccounts.some((account) => marketAccounts.includes(account.col)))  {
                         marketDataFetched = await Promise.all(
                            selectedCompanies.slice(0, 5).map(async (company) => {
                                if (marketData.some((data) => data.secCode === company.secCode)) {
                                    // 既に存在するデータを使用
                                    return marketData.find(data => data.secCode === company.secCode);
                                } else {
                                    // APIリクエストで新しいデータを取得]
                                    try {
                                        const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/stock-price-time-series/${company.secCode}/`);
                                        const sortedData = response.data.slice().reverse();
                                        return { secCode: company.secCode, data: sortedData };
                                    } catch (error) {
                                        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                                            navigate('/login', { state: { from: location.pathname } });
                                        } else {
                                            console.error('Error fetching market data:', error);
                                        }
                                    }
                                }
                            })
                        );
                        // 一度に financialData をセット
                        setMarketData((prevMarketData) => [
                            ...prevMarketData,
                            ...marketDataFetched.filter(
                                (newData) => !prevMarketData.some((data) => data.secCode === newData.secCode)
                            ),
                        ]);
                    }

                    let dividendDataFetched;
                    if (selectedAccounts.some((account) => dividendAccounts.includes(account.col)))  {
                        dividendDataFetched = await Promise.all(
                           selectedCompanies.slice(0, 5).map(async (company) => {
                               if (dividendData.some((data) => data.secCode === company.secCode)) {
                                   // 既に存在するデータを使用
                                   return dividendData.find(data => data.secCode === company.secCode);
                               } else {
                                   // APIリクエストで新しいデータを取得]
                                   try {
                                       const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/dividend-time-series/${company.secCode}/`,
                                            {params:
                                                {
                                                    total_only: true,
                                                }
                                            }
                                       );
                                       const sortedData = response.data.slice().reverse();
                                       return { secCode: company.secCode, data: sortedData };
                                   } catch (error) {
                                       if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                                           navigate('/login', { state: { from: location.pathname } });
                                       } else {
                                           console.error('Error fetching dividend data:', error);
                                       }
                                   }
                               }
                           })
                       );
                       // 一度に financialData をセット
                       setDividendData((prevDividendData) => [
                           ...prevDividendData,
                           ...dividendDataFetched.filter(
                               (newData) => !prevDividendData.some((data) => data.secCode === newData.secCode)
                           ),
                       ]);
                   }

                    // グラフデータを作成
                    const newGraphData = [];
                    for (let i = 0; i < selectedAccounts.length; i++ ) {
                        const unit = selectedAccounts[i].unit;
                        if (marketAccounts.includes(selectedAccounts[i].col)) {
                            const chartData = createMarketGraphData(selectedCompanies, marketDataFetched, selectedAccounts[i], unit, selectedItem);
                            newGraphData.push(chartData);
                        } else if (dividendAccounts.includes(selectedAccounts[i].col)) {
                            const chartData = createDividendGraphData(selectedCompanies, dividendDataFetched, selectedAccounts[i], unit, selectedItem);
                            newGraphData.push(chartData);
                        } else {
                            const chartData = createGraphData(selectedCompanies, financialDataFetched, selectedAccounts[i], unit, selectedItem);
                            newGraphData.push(chartData);
                        }
                    };
                    setGraphData(newGraphData);
                    // 最後に一度だけセット
                    
                } catch (error) {
                    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                        navigate('/login');
                    } else {
                        console.error('Error fetching latest financial results:', error);
                    }
                }
            } else if (selectedItem === 'scatter' & selectedAccounts.length >= 2 && selectedCompanies.length > 0) {
                const fetchLatestMarketData = async (col) => {
                    const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/bulk-latest-market-data/`, {
                        params: {
                            sec_codes: selectedCompanies.map((company) => company.secCode),
                            columns: col === 'stock_price' ? ['close'] : [col]
                        },
                        paramsSerializer: (params) => {
                            // 配列形式のパラメータを正しくシリアライズ
                            return Object.keys(params)
                                .map((key) => {
                                    if (Array.isArray(params[key])) {
                                        return params[key]
                                            .map((val) => `${key}[]=${val}`)
                                            .join("&");
                                    }
                                    return `${key}=${params[key]}`;
                                })
                                .join("&");
                        },
                    })
                    return response.data
                }

                const fetchFinancialData = async (col) => {
                    const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/bulk-financial-results/`, {
                        params: {
                            sec_codes: selectedCompanies.map((company) => company.secCode),
                            columns: [col]
                        },
                        paramsSerializer: (params) => {
                            // 配列形式のパラメータを正しくシリアライズ
                            return Object.keys(params)
                                .map((key) => {
                                    if (Array.isArray(params[key])) {
                                         return params[key]
                                            .map((val) => `${key}[]=${val}`)
                                            .join("&");
                                    }
                                    return `${key}=${params[key]}`;
                                })
                                .join("&");
                        },
                    })
                    return response.data;
                }


                const fetchData = async () => {
                    try {
                        // すべての選択された企業の financialData を並行して取得
                        const results = await Promise.all(
                            selectedAccounts.map(async (account) => {
                                if (marketAccounts.includes(account.col)) {
                                    const data = await fetchLatestMarketData(account.col);
                                    return data;
                                } else {
                                    const data = await fetchFinancialData(account.col);
                                    return data;
                                }
                            })
                        );
                        return results;
                    } catch (error) {
                        console.error("Error fetching data:", error);
                    }
                };

                const fetchedData = await fetchData()   
                    const newGraphData = [];
                    if (selectedAccounts.length == 2) {
                        const units = selectedAccounts.map((account) => (account.unit))
                        const chartData = createScatterGraphData(selectedCompanies, fetchedData, selectedAccounts, units, selectedItem);
                        newGraphData.push(chartData);
                    }
                    setScatterGraphData(newGraphData);
            }
        };

        // 非同期処理の実行
        fetchAndProcessData();
    }, [selectedAccounts, selectedCompanies, navigate, selectedItem, selectedScatterAccounts]);
    

    /* Scatter Graph用のデータを作る */
    const createScatterGraphData = (selectedCompanies, fetchedData, accounts, units, graphType) => {
        const chartData = {}
        const xData = fetchedData[0]
        const xAccount = accounts[0]
        const yData = fetchedData[1]
        const yAccount = accounts[1]
        chartData['data'] = selectedCompanies.map((company) => {
            const x = xData.find((data) => data.sec_code === company.secCode);
            let xValue;
            let yValue; 
            if (x) {
                xValue = x[xAccount.col === 'stock_price' ? 'close' : xAccount.col]
            } else {
                xValue = null
            }

            const y = yData.find((data) => data.sec_code === company.secCode);
            if (y) {
                yValue = y[yAccount.col === 'stock_price' ? 'close' : yAccount.col]
            } else {
                yValue = null
            }
            
            if (xValue && yValue) {
                return {
                    x: xValue,
                    y: yValue,
                    xTiming: marketAccounts.includes(xAccount.col) ? x.date : x.fiscal_year,
                    yTiming: marketAccounts.includes(yAccount.col) ? y.date : y.fiscal_year,
                    xUnit: xAccount.unit === 'value' ? '円' : xAccount.unit,
                    yUnit: yAccount.unit === 'value' ? '円' : yAccount.unit,
                };
            }
        })
        chartData['unit'] = units;
        chartData['name'] = [xAccount.col_name, yAccount.col_name];
        chartData['graphType'] = graphType;
        chartData['companies'] = selectedCompanies;
        return chartData;
    }
    
    /* グラフ用のデータを作る */
    const createGraphData = (selectedCompanies, financialDataFetched, account, unit, graphType) => {
        const selectedSecCodes = selectedCompanies.map((company) => company.secCode);
        const yearMap = {};
        const divUnits = {};
        const convertedUnits = {};
        const limitedCompanies = [];
        const currencies = [];
        let processedCount = 0; // 処理した企業数をカウント
    
        // **1. 全年度を事前に収集**
        const allYears = new Set();
        financialDataFetched.forEach((company) => {
            if (selectedSecCodes.includes(company.secCode)) {
                company.data.forEach((entry) => {
                    allYears.add(entry.fiscal_year);
                });
            }
        });
    
        // `yearMap` を全年度で初期化
        const sortedYears = Array.from(allYears).sort(); // 年度をソート
        sortedYears.forEach((year) => {
            yearMap[year] = { x: year }; // 年度ごとに初期化
        });
    
        // **2. 企業ごとのデータを格納**
        for (let i = 0; i < financialDataFetched.length; i++) {
            const company = financialDataFetched[i];
            if (processedCount >= 5) break; // 5件に達したら中断
    
            if (selectedSecCodes.includes(company.secCode)) {
                limitedCompanies.push(selectedCompanies.find(c => c.secCode === company.secCode));
                
                // 各企業のデータを年度ごとに格納
                company.data.forEach((entry) => {
                    const year = entry.fiscal_year;
                    yearMap[year][company.secCode] = entry[account.col];
                });
    
                // 値を変換して、単位を計算
                const values = company.data.map((entry) => entry[account.col]);
                const converted = convertCurrency(values);
                const divUnit = converted.divValue;
                const convertedUnit = converted.unit;
    
                divUnits[company.secCode] = divUnit;
                const currency = company.data[0].reported_currency;
                currencies.push(currency);
    
                const displayUnit = unit === 'value' ? currencyToSymbol(currency) : unit;
                convertedUnits[company.secCode] = convertedUnit + displayUnit;
    
                processedCount++;
            }
        }
    
        // **3. 欠けているデータをnullで埋める**
        Object.values(yearMap).forEach((entry) => {
            selectedSecCodes.forEach((secCode) => {
                if (entry[secCode] === undefined) {
                    entry[secCode] = null; // データがない場合はnullを設定
                }
            });
        });
    
        // **4. チャート用のデータを作成**
        const chartData = {
            data: Object.values(yearMap),
            unit: convertedUnits,
            unitX: '年',
            divUnit: divUnits,
            currency: currencies,
            col: account.col,
            name: account.col_name,
            graphType: graphType,
            dataCategory: 'financial',
            companies: limitedCompanies,
        };
    
        return chartData;
    };
    
    
    const SelectTodoContainer = () => {
        return (
            <div className="graph-type-selection-container">
                <div id="bar" className={`graph-type graph-modal-icon ${selectedItem=='bar' ? 'bg-gray-400' : ''}`} data-tooltip="棒グラフ" onClick={() => itemClick('bar')}>
                    <i className="fa-solid fa-chart-column"></i>
                </div>
                <div id="line" className={`graph-type graph-modal-icon ${selectedItem=='line' ? 'bg-gray-400' : ''}`} data-tooltip="線グラフ" onClick={() => itemClick('line')}>
                    <i className="fa-solid fa-chart-line"></i>
                </div>
                <div id="scatter" className={`flex items-center content-center graph-type graph-modal-icon ${selectedItem=='scatter' ? 'bg-gray-400' : ''}`} data-tooltip="散布図" onClick={() => itemClick('scatter')}>
                    <ScatterChart />
                </div>
                <div id="chat" className={`graph-type graph-modal-icon  ${selectedItem=='chat' ? 'bg-gray-400' : ''}`} data-tooltip="チャット" onClick={() => itemClick('chat')}>
                    <i className="fa-regular fa-comment"></i>
                </div>
                {/* <div id="make-report" className={`graph-type graph-modal-icon ${selectedItem=='make-report' ? 'selected' : ''}`} data-tooltip="レポート作成" onClick={() => itemClick('make-report')}>
                    <i className="fa-solid fa-file-pen"></i>
                </div> */}
            </div>
        )
    }

    /* 既存のノートに保存する */
    const onSaveToExistingNotebookClick = async (noteId) => {
        const chatHistoryId = selectedChatHistoryId;
        let selectedGraphData;
        if (selectedItem === 'scatter') {
            selectedGraphData = scatterGraphData[0];
        } else {
            selectedGraphData = graphData[chatHistoryId];
        }
        
        try {
            const response = await api.post('/chat/api/save-to-note/', {
                chatId,
                chatHistoryId,
                noteId,
                selectedGraphData,
            });
        if (response.ok) {
        } else {
            console.error('Error saving feedback');
        }
        } catch (error) {
            console.error('Error saving eisting note', error);
        }
    }

    /* 新しいノートを作成する */
    const onCreateNewNotebookClick = async (title) => {
        const response = await api.post('/chat/api/new-note/', {
        title,
        });
        if (notebookListRef.current) {
            notebookListRef.current.fetchNotebookList();  // NotebookListコンポーネントのメソッドを呼び出してリストを更新
        }
    }

    // ブックマークボタン押下時の処理
    const handleBookmarkClick = (historyId) => {
        setShowNotebookList(true);
        setSelectedChatHistoryId(historyId);
    }

    // chat history ID の更新
    const updateLatestChatHistoryId = () => {
        setLatestChatHistoryId(prev => prev + 2);
    }

    const SelectedAccountContainer = () => {
        return (
            <div className="selected-accounts-container">
                {selectedAccounts.map((account) => (
                    <div key={account.col} className="selected-account" >{account.col_name} <div onClick={() => removeAccount(account.col)}>×</div></div>
                ))}
            </div>
        )
    }

    const ScatterSelectedAccountContainer = () => {
        return (
            <div className="selected-accounts-container">
                <div className='selected-axis'>
                    <div className="axis-name">横軸:</div>
                    <div className="selected-account" >{selectedAccounts[0].col_name} <div onClick={() => removeAccount(selectedAccounts[0].col)}>×</div></div>
                </div>
                <div className='space10'></div>
                {selectedAccounts.length >= 2 && 
                    <div className='selected-axis'>
                        <div className="axis-name">縦軸:</div>
                        <div className="selected-account" >{selectedAccounts[1].col_name} <div onClick={() => removeAccount(selectedAccounts[1].col)}>×</div></div>
                    </div>
                }
            </div>
        )
    }
    
    const setAccount = (account) => {
        if (selectedItem === 'scatter' && selectedAccounts.length >= 2) {
            // この場合は追加しない
        } else {
            setSelectedAccounts((prevAccounts) => [account, ...prevAccounts])
        }
    }

    const removeAccount = (accountCol) => {
        setSelectedAccounts((prevAccounts) => prevAccounts.filter((acc) => acc.col !== accountCol))
    }
    
    return (
        <div
        className={`${!resized && "w-2/5"} analysis-area rounded-md relative  ${showAnalysisAreaSmall ? 'absolute' : ''}`}
        style={{
            width: showAnalysisAreaSmall
              ? '100%' // モバイル用：表示中なら100%に
              : !resized
                ? ''
                : typeof width === 'string' && width.includes('%')
                  ? `calc(${width} - 20px)`
                  : `${width}px`,
            display: showAnalysisAreaSmall || window.innerWidth > 767 ? 'block' : 'none'
          }}
        ref={widthRef}
        >
            {showAnalysisAreaSmall && window.innerWidth <= 767 && <div className='absolute top-[-30px] right-[5px] py-1 rounded-2xl bg-green-500 w-[130px] text-white align-center' onClick={handleCloseAnalylsis}>分析画面を閉じる</div>}
            <div className='p-[10px] w-full h-full flex flex-col overflow-y-auto'>
            { showNotebookList ? <NotebookList 
                ref={notebookListRef}
                onSaveToExistingNotebookClick={onSaveToExistingNotebookClick}  
                onCreateNewNotebookClick={onCreateNewNotebookClick}  
                onCancelClick={() => setShowNotebookList(false)} 
                /> 
                : 
            null }
                
            <AnalysisCompanies selectedCompanies={selectedCompanies} onDeleteClick={onDeleteClick} onDeleteAllClick={onDeleteAllClick} />
            {<SelectTodoContainer />}
            <div className="analysis-inner-area">
                {selectedItem === 'chat' ? 
                    <ChatArea chatId={chatId} latestChatHistoryId={latestChatHistoryId} updateLatestChatHistoryId={updateLatestChatHistoryId} companies={selectedCompanies} onSaveClick={handleBookmarkClick} />
                : 
                    <>
                        <SelectColumnContainer selectedAccount={selectedAccounts} setSelectedAccount={setAccount} clearSearchTerm={true} />
                            {selectedItem !== 'scatter' && selectedAccounts.length > 0 && <SelectedAccountContainer />}
                            {selectedItem === 'scatter' && selectedAccounts.length > 0 && <ScatterSelectedAccountContainer />}
                        <AnalysisGraphArea 
                            selectedCompanies={selectedCompanies}
                            selectedItem={selectedItem}
                            selectedAccounts={selectedAccounts}
                            graphData={graphData}
                            scatterGraphData={scatterGraphData}
                            handleBookmarkClick={handleBookmarkClick}
                            width={divWidth}
                        />
                    </>
                }
                </div>
            </div>
        </div>
    )
}

export default AnalysisArea